import { FormattedMessage, useIntl } from 'react-intl';

import { hasAddedLines } from '@/utils/checkout';

import { ProductCard } from '@/components/ProductCard';
import { getProductCardImageAlt } from '@/components/ProductCard/helpers';
import type { TaxedMoneyFragment } from '@/graphql/saleor/api';
import { useRouter } from '@/hooks/useRouter';
import { useStrapPurchaseFlow } from '@/hooks/useStrapPurchaseFlow';
import { type StrapHit } from '@/integrations/algolia/types';
import {
  findProductCta,
  getMostRelevantProductCta,
} from '@/integrations/orderApp';
import type { ProductCatalogModeCtaStyle } from '@/lib/types';
import { commonMessages } from '@/messages/common';
import { theme } from '@/theme';
import { AddToBagDrawer } from '@/views/StrapView/components/AddToBagDrawer';

import { useProductListCardCommon } from './helper';

interface StrapCardProps {
  cardClickHandler?: () => void;
  hit: StrapHit;
  isMostRelevantCta?: boolean;
  priority?: boolean;
  productCatalogModeCtaStyle?: ProductCatalogModeCtaStyle;
  sizes?: string;
  strapSizeDatas?: Record<string, any>;
  style?: React.CSSProperties;
  type?: string;
}

export const StrapCard = ({
  cardClickHandler,
  hit,
  isMostRelevantCta = false,
  priority = false,
  productCatalogModeCtaStyle = 'DISCOVER',
  sizes,
  strapSizeDatas,
  style,
  type,
}: StrapCardProps) => {
  const {
    contextProductCatalogMode,
    detailPath,
    endProcessing,
    isLocalProcessing,
    isProcessing,
    lineAdd,
    name,
    openNotifyMe,
    sendAddedToCartEvent,
    setContactUsModalStatus,
    setIsLocalProcessing,
    startProcessing,
  } = useProductListCardCommon(hit);
  const { push } = useRouter();
  const { formatMessage } = useIntl();

  const skus = hit.variant_group;

  const {
    buckles,
    handlePurchase,
    isDrawerOpen: isAddToBagModalOpen,
    setIsDrawerOpen: setIsAddToBagModalOpen,
    sizeState,
    strapData,
    tools,
  } = useStrapPurchaseFlow(hit.objectID, hit?.variant_id, skus);

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (contextProductCatalogMode) {
      return push(detailPath);
    } else if (cta && ['ORDER', 'PRE_ORDER']?.includes(cta)) {
      setIsLocalProcessing(true);
      startProcessing();
      const shouldOpenDrawer = await handlePurchase(
        sizeState.sizesSelectedItem
          ? (sizeState.sizesSelectedItem.data as { id: string; sku: string })
              ?.sku
          : hit.objectID,
        skus,
      );

      if (shouldOpenDrawer !== false) {
        setIsLocalProcessing(false);
        endProcessing();
        return;
      }

      setIsLocalProcessing(true);
      try {
        const addedLines = await lineAdd(hit?.variant_id, hit?.call_to_action);
        if (hasAddedLines(addedLines)) {
          sendAddedToCartEvent();
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLocalProcessing(false);
        endProcessing();
      }
    } else if (cta === 'NOTIFY') {
      if (hit.objectID) {
        openNotifyMe({ sku: hit.objectID });
      }
    } else if (cta === 'NO_CTA') {
      setContactUsModalStatus(true, hit.objectID);
    }
  };

  if (!hit) return null;

  const strapSku = sizeState.sizesSelectedItem
    ? (sizeState.sizesSelectedItem.data as { id: string; sku: string })?.sku
    : hit.objectID;

  // This return the CTA of the most available strap even if it's not the one displayed only on the PLPs
  const getCta = isMostRelevantCta ? getMostRelevantProductCta : findProductCta;
  const cta = getCta(
    strapSizeDatas,
    sizeState.sizesSelectedItem
      ? (sizeState.sizesSelectedItem.data as { id: string; sku: string })?.sku
      : strapSku,
  );

  return (
    <>
      <ProductCard
        backgroundColor={theme.colors.lightGrey}
        cardClickHandler={cardClickHandler}
        cta={
          (!contextProductCatalogMode ||
            productCatalogModeCtaStyle !== 'HIDDEN') && (
            <ProductCard.CTA
              cta={contextProductCatalogMode ? 'DISCOVER' : cta}
              data-test-id='StrapCardCTA'
              disabled={isProcessing}
              isProcessing={isLocalProcessing}
              onClick={handleClick}
            />
          )
        }
        ctaType='bottom'
        data-test-id='StrapCardProductCard'
        href={detailPath}
        sku={hit.objectID}
        style={style}
        topSlot={
          <ProductCard.Favorite
            data-test-id='StrapCardFavorite'
            name={name}
            price={hit.price}
            sku={hit.objectID}
            theCurrency={hit.currency}
            type={hit.product_type}
          />
        }
        type={hit.product_type}
      >
        <ProductCard.Zone>
          <ProductCard.LugSize data-test-id='StrapCardLugSize'>
            {hit.distance_between_horns}
          </ProductCard.LugSize>
          <ProductCard.Image
            alt={getProductCardImageAlt({
              formatMessage,
              name,
              sku: hit.objectID,
            })}
            className={`${
              type === 'square-strap' ? 'square-strap' : 'strap'
            } strap-card`}
            data-test-id='StrapCardImage'
            loading={priority ? 'eager' : 'lazy'}
            priority={priority}
            productType='strap'
            scale={1}
            sizes={sizes ?? '(max-width: 600px) 50vw, 25vw'}
            src={hit.thumbnail_url as string}
            withBackgroundPlaceholderBackground={false}
          />
        </ProductCard.Zone>
        <ProductCard.Zone>
          <ProductCard.Sku data-test-id='StrapCardSku'>
            {hit.objectID}
          </ProductCard.Sku>
          <ProductCard.Name data-test-id='StrapCardName'>
            {name}
          </ProductCard.Name>
          <ProductCard.Price
            data-test-id='StrapCardPrice'
            price={{ amount: hit.price, currency: hit.currency }}
          />
          {hit.integrated_buckle === false && (
            <ProductCard.AdditionalInfo data-test-id='StrapCardAdditionalInfo'>
              <FormattedMessage {...commonMessages.buckleNotIncluded} />
            </ProductCard.AdditionalInfo>
          )}
        </ProductCard.Zone>
      </ProductCard>
      {strapData.businessData && (
        <AddToBagDrawer
          buckles={buckles}
          businessData={strapData.businessData}
          cta={cta}
          data-test-id='StrapCardAddToBagDrawer'
          isOpen={isAddToBagModalOpen}
          niceToHaveProducts={tools}
          onAddedToBag={sendAddedToCartEvent}
          setIsOpen={setIsAddToBagModalOpen}
          sizeState={sizeState}
          strap={hit}
          strapId={
            sizeState.sizesSelectedItem
              ? sizeState.sizesSelectedItem.value
              : hit?.variant_id
          }
          strapPrice={
            strapData.variantDetailsData?.productVariant?.pricing
              ?.price as TaxedMoneyFragment
          }
          strapSku={
            sizeState.sizesSelectedItem
              ? (
                  sizeState.sizesSelectedItem.data as {
                    id: string;
                    sku: string;
                  }
                )?.sku
              : hit?.objectID
          }
        />
      )}
    </>
  );
};
